// The stuck-PR RESCUE (#6172, widened by #6231) — one more round, on the escalation agent's
// stronger model, for an AI-managed PR whose head has made no progress for `STUCK_PR_RESCUE_AFTER_MS` (3h) and that
// parked on a park a coding agent can still fix. Its own module because `./decide.ts` is past
// the 800-line budget, and because it is not a rung of the ladder: it reads the ladder's
// verdict and decides whether that verdict still stands.
//
// THE GAP. #6149 removed the count budgets on an AI-managed PR (`aiManagedPr`: our App's or
// a dependency bot's, on an auto-merge repo) and named the parks still reaching a human on
// one. Once one parked the PR, nothing machine-side tried again — and every round that
// failed ran on the account's DEFAULT model. The model escalation ladder (`apps/worker`
// `dispatch/model-escalation.ts`) runs a task on the account's stronger model, but it fired
// only on a coding task whose resumed gate stalled, and a parked PR produces no such task.
//
// THE RULE. The decision is `escalate(reason)` on an AI-managed PR, `reason` is one of
// `STUCK_PR_RESCUE_MINT_REASONS`, and the PR's head has been idle {@link STUCK_PR_RESCUE_AFTER_MS}
// or more (`BabysitBookkeeping.stuckIdleMs`): the escalation becomes ONE round of the
// reason's own action, marked `rescue`. The mint stamps the mark on the follow-up's spec,
// which is what sends its claim up the escalation ladder from the first attempt.
//
//   merge_conflict                          -> resolve_conflict
//   ci_not_converging, ci_fix_declined      -> a CI fix_pr
//   findings_not_converging                 -> a findings-only fix_pr
//   max_sweeps_exceeded                     -> the round of its `ladder` (`SweepsLadder`);
//                                              `comments` stands: a person is in that loop
//   followup_held                           -> the park's OWN held round, re-filed as a
//                                              superseding rescue (#6344) — see below
//   own_review_hold_stale, findings_unresolved -> a findings-only fix_pr (#6345); an own
//                                              hold with nothing to fix returns the
//                                              escalation MARKED `rescue` — the worker
//                                              spends the mark on one fresh re-review
//   pr_intent_reverted                      -> a CI fix_pr, else a findings-only one
//                                              (#6346); the worker's brief leads with
//                                              "the previous round undid this PR's change"
//                                              and the harness still refuses a revert
//   ci_wait_timeout, ci_cancelled           -> ONE `rerun_checks` hop in rescue mode
//                                              `cancel_then_rerun` (#6346) — see below
//
// THE CI RE-RUN (#6346). A check hung past the CI ceiling, or a job with no verdict past its
// one re-run, is not the code's failure, so its rescue is no round: it is one re-trigger of
// the head's CI, the executor cancelling any named run still in progress and then re-running
// it (GitHub re-runs completed runs only). Its reasons (`CI_RERUN_RESCUE_REASONS`) are kept
// out of the stored `pr_babysit` rescue vocabulary — no spec carries the mark; the hop row
// does, and that row is its ledger in `rescuedReasons`. A park naming no run we can re-run
// (only a required check that never registered, or a no-verdict check that is not a GitHub
// Actions job) has no rescue and stands.
//
// THE HELD-ROUND REFILE (#6344). `followup_held` is the one reason whose round is not
// computed from the observation: it is the very action the held-round park (#6341,
// `./in-flight.ts`) replaced, carried on `decision.behind`, re-minted with
// `supersedes: <held row>` so the executor retires the held row in the mint's own
// transaction (`supersedeAndMintBabysitRound`, `@developerz/db`). Since #6936 it re-files
// on ANY hold but `HELD_ROUND_TERMINAL_HOLDS`: a hold that is the row's OWN spent budget
// (`TASK_HOLD_REFILE`'s `fresh_row_clears`) starts clean on the fresh row, and an INHERITED
// one (`fresh_row_inherits`) is re-derived for it by the dispatcher's next tick — a stamp
// that no longer holds clears, a live one re-stamps and the park re-forms past the
// ceiling. #6344 refused the inherited ones as churn, and that refusal is what left
// developerz-ai/ultimate#816 parked `followup_held` with nothing ever re-trying. A
// terminal hold (the repo is not onboarded, the account's seats are capped, the account
// switched the mode off) re-stamps for certain and is the account's to lift: it stands
// and alarms. A held round that is itself a rescue stands: a rescue supersedes a row,
// never another rescue — without that, a re-held rescue would be superseded every pass,
// since a LIVE rescue row is not yet in `rescuedReasons`. `followup_unrepeatable` is NOT minted
// here — no decision escalates it; the executor mints that rescue at the id collision it
// answers (`apps/worker` `followup-executors.ts`).
//
// THE STORED SET IS WIDER THAN THE MINT SET (#6343). `STUCK_PR_RESCUE_REASONS` carries
// members the read side must decode before anything writes them; `rescuable` gates on
// `STUCK_PR_RESCUE_MINT_REASONS` so widening the vocabulary a lagging box must tolerate
// cannot make this decision EMIT a member no release has wired a round for (the stored
// closed-set rule, `packages/db/docs/migration-rules.md`). #6344 moved `followup_held`
// and `followup_unrepeatable` into the mint set, and #6345 moved `own_review_hold_stale`
// and `findings_unresolved`; #6346 moved the last #6338 member, `pr_intent_reverted`.
//
// ONE CLOCK (#6231). "No progress" is the head's idle time: the first babysit hop that saw
// THIS head, anchored after the last maintainer re-arm. Hourly re-observations never move
// it; a push or a re-arm restarts it, a babysit BASE UPDATE does not (#7015, owner ruling
// ivndev001 2026-10-23: `stuckIdleMs` walks back to the head it was updated from, while
// every other wait keeps the current head's `watchIdleMs`). It replaced a park clock (the first escalation row for
// the reason), which diverged from it exactly when a round pushed and the PR re-parked —
// and that push is progress. So the rescue fires on the first escalate hop of a head idle
// 12h+, with no park on record at all.
//
// ONCE per (PR, reason) since the last re-arm (`rescuedReasons`). The bound names its
// failure: a PR that re-parks after the stronger model had a full round at it will not
// converge on another identical round. The escalation that then stands is not the end: a
// machine re-arm (`./machine-rearm.ts`, at most `MACHINE_REARMS_PER_PR` per PR) opens a
// fresh window for a machine-resolvable park, and once those are spent the standing close
// (`./standing-close.ts`) closes the PR and re-files its goal. A re-arm — the maintainer's
// or the machine's — resets the window, as it does for the escalation once-gate.
//
// THE SAME ANSWER AGAIN. A `ci_fix_declined` rescue round buys the very answer the
// declined round already gave: the same head-diff identity and the same failing-check set
// (`./decline-evidence.ts`). While the decline on record is UNCHANGED, that round is
// REFUSED and the escalation stands — the machine re-arm and the standing close are the
// ways out of it, not a second round on the same diff. A decline whose head or whose
// failing set CHANGED is a different answer, and buys the rescue round as before.
//
// NOT pre-checked against the pool: when the account names no stronger model the round
// still runs once, on the default, and the ladder records `no_stronger_model` on the
// account's audit trail. A second copy of the tier ranking here would drift from the one
// the claim uses; naming an `escalation` route is the account's lever.

import {
  STUCK_PR_RESCUE_MINT_REASONS,
  type StuckPrMintReason,
} from '../../value-objects/task-envelope';
import { TaskHoldReason } from '../task/hold';
import type {
  BabysitAction,
  BabysitBookkeeping,
  BabysitDecision,
  CiRerunRescue,
  StuckPrRescue,
} from './action';
import { BabysitState } from './action';
import { isCancelledCheck } from './cancelled-checks';
import { declinedEvidenceUnchanged, failingCheckNames } from './decline-evidence';
import {
  type CiRerunRescueReason,
  type EscalationReason,
  isCiRerunRescueReason,
} from './escalation-reason';
import type { BabysitObservation } from './observation';
import { aiManagedPr, type BabysitPolicy } from './policy';

/**
 * How long an AI-managed PR's head must have gone without progress before a rescuable park
 * earns its rescue round (#6172): 3 hours (owner ruling ivndev001 2026-11-03, was 12h).
 * The goal is that no AI-managed PR whose blocker a machine can act on sits 12h+: the
 * rescue, its machine re-arms and the 6h alarm all have to fit inside that, so the first
 * rescue cannot wait 12h itself. It sits one hour PAST the 120-min wait bounds
 * (`CI_WAIT_CEILING_MS`) on purpose: at 2h the rescue fired on the same pass as the first
 * escalation and spent the PR's one rescue on the ordinary first action (a never-reviewed
 * head's first review), leaving none for a real stall. Not a spend bound. A park the machine may move is
 * re-observed every 15 min while parked (`driftThresholdFor`,
 * `apps/worker/src/babysit/debounce.ts`), so the round lands within a sweep of the mark.
 */
export const STUCK_PR_RESCUE_AFTER_MS = 3 * 60 * 60_000;

/**
 * The holds a held round is NEVER re-filed past (#6936): each re-stamps the fresh row for
 * certain and is the account's to lift, so the park stands and alarms. Exported because the
 * machine re-arm (`./machine-rearm.ts`) reads the same set.
 */
export const HELD_ROUND_TERMINAL_HOLDS: readonly TaskHoldReason[] = [
  TaskHoldReason.RepoNotOnboarded,
  TaskHoldReason.SeatCapped,
  TaskHoldReason.ModeDisabled,
  // Every online machine has the task's role switched off (#7008): the account lifts it, and
  // it re-stamps a refiled row for certain.
  TaskHoldReason.NoRoleMachine,
];

/** Is this escalation one a rescue round can answer — and THIS RELEASE may mint? Typed as a predicate so the tuple stays a subset of {@link EscalationReason}. */
function rescuable(reason: EscalationReason): reason is StuckPrMintReason {
  return (STUCK_PR_RESCUE_MINT_REASONS as readonly string[]).includes(reason);
}

/** The round a rescue buys, before it is marked — or `null` when its ladder has nothing to run. */
type RescueRound =
  | Omit<Extract<BabysitAction, { kind: 'resolve_conflict' }>, 'rescue'>
  | Omit<Extract<BabysitAction, { kind: 'fix_pr' }>, 'rescue'>;

/** The conflict ladder's round, on the mint rung — never the spent budget (#4289, `./conflict.ts`). */
function conflictRound(book: BabysitBookkeeping): RescueRound {
  return { kind: 'resolve_conflict', attempt: book.conflictResolveMintAttempts + 1 };
}

/**
 * The CI ladder's round: the failing checks plus what `decideCiRed` would carry — every
 * finding of ours but a decline our reviewer has not yet ruled on
 * (`PrFinding.declineAwaitingRecheck`). `null` with no failing check on the head: a CI round
 * names the checks it fixes, so there is none to run.
 */
function ciRound(obs: BabysitObservation, book: BabysitBookkeeping): RescueRound | null {
  const failedChecks = failingCheckNames(obs.checks);
  if (failedChecks.length === 0) return null;
  return {
    kind: 'fix_pr',
    attempt: book.ciFixAttempts + 1,
    failedChecks,
    findings: obs.findings.filter((f) => !f.declineAwaitingRecheck),
    summaryFindings: obs.platformReviewSummaryFindings,
  };
}

/**
 * The findings ladder's round (#6231): no failed check, our open threads but a decline
 * awaiting the re-check, and our review's summary findings — the round `decideManagedFindings`
 * mints, carrying the threads it stopped buying rounds for. The rungs that escalate its
 * reasons are NOT all past `DEFAULT_MAX_FEEDBACK_ATTEMPTS`: only the per-head rungs are
 * (the decline-awaiting, convergence-stop and sweep-ceiling rungs fire at any
 * `findingsFixAttempts` — `overAttemptCeiling`'s `fix_pr` bullet, `./in-flight.ts`). The
 * past-ceiling round is admitted only because it is marked `rescue`; under the ceiling
 * the mark merely agrees with the general branch. `null` when there is nothing of ours
 * to fix.
 */
function findingsRound(obs: BabysitObservation, book: BabysitBookkeeping): RescueRound | null {
  const findings = obs.findings.filter((f) => !f.declineAwaitingRecheck);
  const summaryFindings = obs.platformReviewSummaryFindings;
  if (findings.length === 0 && summaryFindings.length === 0) return null;
  return {
    kind: 'fix_pr',
    attempt: book.findingsFixAttempts + 1,
    failedChecks: [],
    findings,
    summaryFindings,
  };
}

/** The round each rescuable escalation buys — see the module header's table. */
function roundFor(
  action: Extract<BabysitAction, { kind: 'escalate' }> & { readonly reason: StuckPrMintReason },
  obs: BabysitObservation,
  book: BabysitBookkeeping,
): RescueRound | null {
  switch (action.reason) {
    case 'merge_conflict':
      return conflictRound(book);
    case 'ci_not_converging':
    case 'ci_fix_declined':
      return ciRound(obs, book);
    case 'findings_not_converging':
    case 'own_review_hold_stale':
    case 'findings_unresolved':
      // #6345: the round our own reviewer's hold still owes. `null` when nothing of ours
      // is left to fix — for `own_review_hold_stale` the marked escalate below turns that
      // into one fresh re-review; `findings_unresolved` keeps the escalation as it stands.
      return findingsRound(obs, book);
    case 'pr_intent_reverted':
      // #6346: the round the refused one was — CI first, as `decideCiRed` carries findings
      // on it; with CI green, the findings our reviewer still holds the merge on.
      return ciRound(obs, book) ?? findingsRound(obs, book);
    case 'max_sweeps_exceeded':
      // The site names its ladder (`SweepsLadder`); absent or `comments` buys nothing.
      if (action.ladder === 'conflict') return conflictRound(book);
      if (action.ladder === 'ci') return ciRound(obs, book);
      if (action.ladder === 'findings') return findingsRound(obs, book);
      return null;
    case 'followup_held':
    case 'followup_unrepeatable':
      // Never bought here: `followup_held` rides the park's `behind` round (the refile
      // below intercepts it before this switch), and NO decision escalates
      // `followup_unrepeatable` at all — the executor mints that rescue at the id
      // collision it answers (#6344).
      return null;
  }
}

/**
 * The followup_held refile (#6344, widened by #6936): the park's own held round, re-minted
 * as a rescue that SUPERSEDES the held row — or `null` when the park stands. It re-files on
 * any hold but {@link HELD_ROUND_TERMINAL_HOLDS}, never on a rescue-marked round (a rescue supersedes a row, not another rescue), and never without
 * the park's `behind` (a `followup_held` escalation that did not come from the park names
 * no row to retire). See the module header.
 */
function heldRefile(
  decision: BabysitDecision,
  reason: 'followup_held',
  idleMs: number,
): BabysitDecision | null {
  const behind = decision.behind;
  if (behind === undefined || behind.held !== 'followup_held') return null;
  const hold = behind.round.holdReason;
  if (behind.round.rescue !== null || hold === null || HELD_ROUND_TERMINAL_HOLDS.includes(hold)) {
    return null;
  }
  const mint = behind.action;
  // The park only replaces the three minting kinds (`parkBehindInFlight`); the narrowing
  // is the guard, and each of the three carries an optional `rescue` mark.
  if (
    mint.kind !== 'fix_pr' &&
    mint.kind !== 'resolve_conflict' &&
    mint.kind !== 'address_comments'
  ) {
    return null;
  }
  return {
    // SPREAD the park: its state is the round's own ladder state — the park never changed
    // it — and its `behind` is what the hop's audit visibility keys on.
    ...decision,
    action: { ...mint, rescue: { reason, idleMs, supersedes: behind.round.id } },
    reason:
      `stuck-PR rescue: no progress for ${Math.floor(idleMs / 3_600_000)}h, round` +
      ` ${behind.round.id} has been held (${hold}) — re-filing it as one fresh round on the` +
      ` escalation model, superseding the held row so its hold is derived afresh` +
      ` (${decision.reason})`,
  };
}

/** The checks a CI re-run rescue re-triggers — see {@link CiRerunRescue}. Empty: nothing to re-run. */
function ciRerunTargets(reason: CiRerunRescueReason, obs: BabysitObservation): string[] {
  if (reason === 'ci_wait_timeout') {
    return obs.checks.filter((c) => c.conclusion === 'pending').map((c) => c.name);
  }
  return obs.checks
    .filter(isCancelledCheck)
    .filter((c) => c.rerun.kind !== 'not_rerunnable')
    .map((c) => c.name);
}

/**
 * The CI re-run rescue (#6346): ONE `rerun_checks` hop in rescue mode `cancel_then_rerun`
 * for a `ci_wait_timeout` / `ci_cancelled` park of an AI-managed PR idle
 * {@link STUCK_PR_RESCUE_AFTER_MS}, once per (PR, reason) since the re-arm — or `decision`
 * by identity when it stands. See the module header.
 */
function ciRerunRescue(
  decision: BabysitDecision,
  reason: CiRerunRescueReason,
  obs: BabysitObservation,
  policy: BabysitPolicy,
  book: BabysitBookkeeping,
): BabysitDecision {
  if (!aiManagedPr(obs, policy) || book.rescuedReasons.has(reason)) return decision;
  const idleMs = book.stuckIdleMs;
  if (idleMs === null || idleMs < STUCK_PR_RESCUE_AFTER_MS) return decision;
  const checkNames = ciRerunTargets(reason, obs);
  if (checkNames.length === 0) return decision;
  const rescue: CiRerunRescue = { reason, idleMs, mode: 'cancel_then_rerun', checkNames };
  return {
    state: BabysitState.AwaitingCi,
    action: { kind: 'rerun_checks', checks: [], rescue },
    reason:
      `stuck-PR rescue: no progress for ${Math.floor(idleMs / 3_600_000)}h, parked on` +
      ` ${reason} — cancelling any run of ${checkNames.join(', ')} still in progress and` +
      ` re-running it once before the escalation stands (${decision.reason})`,
  };
}

/**
 * The decision this pass acts on: `decision` itself, unless it is a rescuable park of an
 * AI-managed PR whose head has been idle {@link STUCK_PR_RESCUE_AFTER_MS} and that was not
 * rescued for that reason since the re-arm — then ONE round of the reason's own action,
 * marked `rescue`. See the module header. Pure and idempotent: returns `decision` by
 * identity when the escalation stands — an escalation an earlier pass already marked
 * included, so the multi-pass composition (`gradeHop`) wraps the reason exactly once.
 */
export function rescueStuckPr(
  decision: BabysitDecision,
  obs: BabysitObservation,
  policy: BabysitPolicy,
  book: BabysitBookkeeping,
): BabysitDecision {
  const action = decision.action;
  if (action.kind !== 'escalate') return decision;
  // IDEMPOTENT MARK (#6345 lens r2): a marked escalate is the output of an EARLIER rescue
  // pass in the same grade — `gradeHop` runs this rule three times and the ledger gate
  // below cannot see the mark, because no ledger row exists until the ask lands. Without
  // this identity return every pass re-marked and re-wrapped the reason, and a real run
  // handed the human-facing escalation detail a 727-char text carrying the rescue prefix
  // three times. The mark is the rescue having been decided; the decision stands as-is.
  if (action.rescue !== undefined) return decision;
  const reason = action.reason;
  if (isCiRerunRescueReason(reason)) return ciRerunRescue(decision, reason, obs, policy, book);
  // A rescue is a round, and no round pushes to a head off the base repo (`headRepo`, #7786).
  if (
    !rescuable(reason) ||
    obs.headRepo !== 'same' ||
    !aiManagedPr(obs, policy) ||
    book.rescuedReasons.has(reason)
  ) {
    return decision;
  }
  const idleMs = book.stuckIdleMs;
  if (idleMs === null || idleMs < STUCK_PR_RESCUE_AFTER_MS) return decision;
  // THE SAME ANSWER AGAIN (see the header): the rescue round would buy the answer the
  // declined round already gave, so it is not bought and the escalation stands.
  if (
    reason === 'ci_fix_declined' &&
    declinedEvidenceUnchanged(
      book.ciFixDeclinedEvidence,
      book.headSha ?? '',
      failingCheckNames(obs.checks),
    )
  ) {
    return decision;
  }
  if (reason === 'followup_held') {
    // The refile rides the park's own `behind` round, never `roundFor`: the round to mint
    // is the very action the park replaced (#6344).
    return heldRefile(decision, reason, idleMs) ?? decision;
  }
  const round = roundFor({ ...action, reason }, obs, book);
  if (round === null) {
    // #6345: an `own_review_hold_stale` with nothing to fix — a hold with no open finding
    // and no summary finding of ours: verdict-only, screen-forced, or decline-awaiting
    // (`findingsRound`'s null answer, above) — still has one move before the escalation
    // stands or re-notifies: ONE fresh re-review of the live head. NOT "before a human is
    // asked": the reason escalates and mails at the ~120-min wait bound
    // (`reviewWaitExceeded`, `../ci-wait.ts`) and is not self-clearing, so its first
    // telling went out ~10h before this 12h rescue fires. Hand the escalation back
    // MARKED; the driver's arm spends the mark (`rescueReview`,
    // `apps/worker/src/babysit/idle-review.ts`), and the review task's row is the ledger
    // keeping it once per (PR, reason, re-arm). Every other reason with no round keeps
    // the escalation exactly as it stands.
    if (reason === 'own_review_hold_stale') {
      return {
        ...decision,
        action: { ...action, rescue: { reason, idleMs } },
        reason:
          `stuck-PR rescue: no progress for ${Math.floor(idleMs / 3_600_000)}h, parked on` +
          ` ${reason} — no findings to fix; one fresh re-review of the live head before` +
          ` the escalation stands (${decision.reason})`,
      };
    }
    return decision;
  }
  const rescue: StuckPrRescue = { reason, idleMs };
  return {
    // The ladder's own state for the round: a CI or conflict round waits on CI, a
    // findings-only round on our reviewer, as `decideManagedFindings` files it.
    state:
      round.kind === 'fix_pr' && round.failedChecks.length === 0
        ? BabysitState.AwaitingReview
        : BabysitState.AwaitingCi,
    action: { ...round, rescue },
    reason:
      `stuck-PR rescue: no progress for ${Math.floor(idleMs / 3_600_000)}h, parked on` +
      ` ${reason} — one more round on the escalation model before the escalation stands` +
      ` (${decision.reason})`,
  };
}
